import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiShield, FiZap, FiMessageSquare, FiUser, FiWind, FiUsers, 
  FiCpu, FiDownload, FiPrinter, FiPlay, FiChevronRight, FiCheckCircle 
} = FiIcons;
import { WORKSHOP_TYPES, getAgendaByType } from '../data/workshopTemplates';
import { motion, AnimatePresence } from 'framer-motion';

export const WorkshopFacilitator = () => {
  const [selectedType, setSelectedType] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAgenda, setGeneratedAgenda] = useState(null);

  const handleGenerate = (type) => {
    setSelectedType(type);
    setIsGenerating(true);
    setGeneratedAgenda(null);

    // Simuler AI-generering
    setTimeout(() => {
      setGeneratedAgenda(getAgendaByType(type.id));
      setIsGenerating(false);
    }, 1500);
  };

  return (
    <div className="space-y-8 pb-20">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">AI Facilitator</span>
        </div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase">Workshop Designer</h1>
        <p className="text-slate-500 font-medium">Forvandle innsikt til pedagogiske handlingsplaner</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar: Typer */}
        <div className="lg:col-span-4 space-y-4">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Velg Workshop-fokus</h3>
          <div className="grid gap-3">
            {WORKSHOP_TYPES.map((type) => {
              const IconComp = FiIcons[type.icon] || FiUsers;
              const isSelected = selectedType?.id === type.id;
              
              return (
                <button
                  key={type.id}
                  onClick={() => handleGenerate(type)}
                  className={`group relative overflow-hidden p-4 rounded-2xl border-2 transition-all text-left ${
                    isSelected 
                    ? 'border-indigo-500 bg-indigo-50/50 shadow-lg shadow-indigo-100' 
                    : 'border-slate-100 bg-white hover:border-indigo-200 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl transition-colors ${
                      isSelected ? 'bg-indigo-500 text-white' : 'bg-slate-50 text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-500'
                    }`}>
                      <SafeIcon icon={IconComp} size={20} />
                    </div>
                    <div>
                      <h4 className={`font-bold text-sm ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>
                        {type.title}
                      </h4>
                      <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
                        {type.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main: Agenda Preview */}
        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            {!selectedType ? (
              <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="h-full min-h-[500px] border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center p-12 text-center"
              >
                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-300 mb-4">
                  <SafeIcon icon={FiCpu} size={32} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Klar til å generere</h3>
                <p className="text-slate-400 text-sm max-w-xs mt-2">
                  Velg en kategori til venstre for å la AI-en skreddersy en agenda basert på teamets data.
                </p>
              </motion.div>
            ) : isGenerating ? (
              <motion.div 
                key="loader"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="h-full min-h-[500px] bg-white rounded-3xl shadow-xl flex flex-col items-center justify-center p-12"
              >
                <div className="relative">
                  <div className="w-20 h-20 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center text-indigo-600">
                    <SafeIcon icon={FiCpu} size={24} />
                  </div>
                </div>
                <h3 className="mt-6 text-lg font-black text-slate-900 uppercase tracking-tight">Analyserer resultater...</h3>
                <p className="text-slate-400 text-sm mt-1 italic">Syr sammen øvelser og diskusjonspunkter</p>
              </motion.div>
            ) : (
              <motion.div 
                key="agenda"
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden"
              >
                {/* Header */}
                <div className="bg-slate-900 p-8 text-white flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-indigo-500 text-[10px] font-black px-2 py-0.5 rounded uppercase">AI-Generert</span>
                      <span className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Varighet: 2-3 Timer</span>
                    </div>
                    <h2 className="text-2xl font-black uppercase tracking-tight">Agenda: {selectedType.title}</h2>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                      <SafeIcon icon={FiDownload} />
                    </button>
                    <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                      <SafeIcon icon={FiPrinter} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 space-y-10">
                  {/* Intro */}
                  <section>
                    <h4 className="flex items-center gap-2 text-xs font-black text-indigo-600 uppercase tracking-widest mb-3">
                      <div className="w-1 h-3 bg-indigo-500 rounded-full" /> Introduksjon
                    </h4>
                    <p className="text-slate-600 leading-relaxed font-medium bg-slate-50 p-6 rounded-2xl">
                      "{generatedAgenda.intro}"
                    </p>
                  </section>

                  {/* Exercises */}
                  <section>
                    <h4 className="flex items-center gap-2 text-xs font-black text-indigo-600 uppercase tracking-widest mb-4">
                      <div className="w-1 h-3 bg-indigo-500 rounded-full" /> Gruppeøvelser
                    </h4>
                    <div className="grid gap-4">
                      {generatedAgenda.exercises.map((ex, idx) => (
                        <div key={idx} className="flex gap-4 p-5 rounded-2xl border border-slate-100 hover:border-indigo-100 hover:bg-slate-50/50 transition-all">
                          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 font-black text-xs">
                            {idx + 1}
                          </div>
                          <div>
                            <h5 className="font-bold text-slate-800">{ex.title}</h5>
                            <p className="text-sm text-slate-500 mt-1">{ex.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Discussion */}
                  <section>
                    <h4 className="flex items-center gap-2 text-xs font-black text-indigo-600 uppercase tracking-widest mb-4">
                      <div className="w-1 h-3 bg-indigo-500 rounded-full" /> Refleksjonsspørsmål
                    </h4>
                    <ul className="space-y-3">
                      {generatedAgenda.prompts.map((p, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-slate-700 font-bold text-sm italic">
                          <SafeIcon icon={FiChevronRight} className="text-indigo-400" />
                          "{p}"
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Plan & Follow-up */}
                  <div className="grid md:grid-cols-2 gap-6 pt-6 border-t border-slate-100">
                    <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100">
                      <h4 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <SafeIcon icon={FiCheckCircle} /> Tiltaksplanlegging
                      </h4>
                      <p className="text-sm font-bold text-emerald-900">{generatedAgenda.action_planning}</p>
                    </div>
                    <div className="p-6 bg-amber-50 rounded-2xl border border-amber-100">
                      <h4 className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <SafeIcon icon={FiPlay} /> Oppfølging
                      </h4>
                      <p className="text-sm font-bold text-amber-900">{generatedAgenda.follow_up}</p>
                    </div>
                  </div>
                </div>

                <div className="p-8 bg-slate-50 border-t border-slate-100 flex justify-end">
                  <button className="bg-slate-900 text-white px-8 py-3 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200">
                    Lagre og Aktiver Sesjon
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default WorkshopFacilitator;